/**
 * File: web/src/lib/useAuthGuard.ts
 * ================================
 *
 * Auth guard hook (proactive re-login)
 *
 * Purpose:
 * - Proactively redirect to login when the token is missing or near expiry.
 * - Keep the user from encountering random 401s mid-workflow (uploads/admin actions).
 *
 * When used:
 * - Called by protected routes after config is loaded.
 *
 * Learner notes:
 * - This app uses "redirect to Hosted UI" rather than silent refresh tokens.
 * - We use a skew window to re-auth slightly early to avoid expiry mid-request.
 *
 * Gotcha:
 * - Browser timers can be throttled in background tabs; visibilitychange helps correctness.
 */

import { useEffect } from "react";
import type { AppConfig } from "./types";
import { clearToken, getToken, login } from "./auth";
import { isTokenExpired, msUntilExpiry } from "./jwt";

const AUTH_CHANGED_EVENT = "auth-changed";

export function useAuthGuard(config: AppConfig | null) {
  useEffect(() => {
    if (!config) return;

    let timer: number | undefined;

    // Clear any prior timeout when we re-arm.
    const disarm = () => {
      if (timer) window.clearTimeout(timer);
      timer = undefined;
    };

    /**
     * Arm the guard:
     * - if token missing, do nothing (route may show a login UI)
     * - if token expired/near-expiry, redirect immediately
     * - otherwise schedule redirect shortly before expiry
     */
    const arm = () => {
      disarm();

      const token = getToken();
      if (!token) return;

      // If token is already expired (or within skew), force re-auth now.
      if (isTokenExpired(token, 30)) {
        clearToken();
        login(config, window.location.pathname);
        return;
      }

      // Schedule redirect shortly before expiry to avoid mid-request expiration.
      const ms = msUntilExpiry(token, 30);
      timer = window.setTimeout(() => {
        clearToken();
        login(config, window.location.pathname);
      }, ms);
    };

    // Arm immediately on mount.
    arm();

    // Re-arm when token changes (setToken/clearToken dispatch this event).
    const onAuthChanged = () => arm();

    // Re-check when tab becomes visible again (sleep/wake or background throttling).
    const onVisibility = () => {
      if (document.visibilityState === "visible") arm();
    };

    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      disarm();
      window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [config]);
}
